// Start states and the test/debug placement path. `coldOpenState` is the fresh-game layout (§0.4 u00: the
// Gallery with Sarge's Cork Popper and the practice tin; the Lead Vein rises at u01). Tests, bots and
// scenarios build boards with the place* helpers BEFORE the Simulation starts (the initial state is part of
// a replay, so logs stay reproducible); in play, buildings arrive through the shop (`drop` from a shop card).
import { isProcessorType, slotFams } from './bdefs.ts';
import { addBuilding, makeDeposit, makeDrill, makeGallery, makeOfType } from './buildings.ts';
import { seedState } from './core/rng.ts';
import type { Dir, Int } from './core/types.ts';
import { type Ctx, makeCtx } from './ctx.ts';
import type { SimDefs } from './defs.ts';
import { isActive } from './defs.ts';
import { addItem, discover, isValidItem, newId } from './entities.ts';
import type { BType, FamId, OreId, UnlockId } from './gen/ids.ts';
import { newGhost } from './ghosts.ts';
import { book, emptyLedger } from './ledger.ts';
import { isFree } from './occupancy.ts';
import { DEFAULT_DIR } from './rules/place.ts';
import { raiseStall } from './stall.ts';
import {
  type Building,
  type DepositB,
  type DrillB,
  type FloorItem,
  type ItemRef,
  type ProcessorB,
  type SimState,
  STATE_VERSION,
} from './state.ts';
import { applyAllUnlocks, applyUnlock, emptyPracticeTin } from './unlocks.ts';

/** dropRng is a separate stream derived from the same seed (§0.3 supply drops, v0.2). */
const DROP_STREAM = 0x5bd1e995;

function zeros(n: Int): Int[] {
  const a: Int[] = [];
  for (let i = 0; i < n; i++) a.push(0);
  return a;
}

/** A board with nothing on it (mask `bounds`, default the starting 3×3). */
export function emptyState(d: SimDefs, seed: Int, bounds: Int = 0): SimState {
  if (!Number.isSafeInteger(seed)) throw new RangeError(`seed must be an integer (${seed})`);
  if (!d.board.expansions[bounds]) throw new RangeError(`no expansion #${bounds}`);
  const ring = d.economy.income.ringBuckets;
  return {
    v: STATE_VERSION,
    tick: 0,
    rng: seedState(seed),
    dropRng: seedState(seed ^ DROP_STREAM),
    nextId: 1,
    bounds,
    floor: [],
    buildings: [],
    wallet: { cash: 0, lifetime: 0, lifetimeAuto: 0 },
    income: { total: zeros(ring), auto: zeros(ring), idx: 0, accT: 0, accA: 0, autoFrom: -1 },
    progress: {
      unlocked: [],
      discovered: [],
      firstBuilt: [],
      goal: -1,
      tutorial: 0,
      handMerges: 0,
      mergeAllReadyAt: 0,
      merges: 0,
      sales: 0,
      holdHits: 0,
      firedBest: 0,
      firedAutoBest: 0,
      madeBy: [],
      bindingT: { starved: 0, gunLimited: 0 },
      seen: [],
    },
    transient: { held: null, hold: null, lastSale: null, undo: [], tapCd: 0, tapBuf: [] },
    ledger: emptyLedger(),
  };
}

export interface StartOptions {
  /** Expansion mask index (default 0 = 3×3). */
  bounds?: Int;
  /** Also apply u01: the Lead Vein rises at (3,4). */
  afterU01?: boolean;
  /** Apply every version-active unlock (debug "unlock all"). */
  unlockAll?: boolean;
  /**
   * Start mid-progression: apply exactly these unlocks (with their effects) in table order. The list must
   * be prefix-closed along `after` ("masks nest"). An unlock whose trigger is `practiceEmpty` (u01)
   * first empties the practice tin, as its trigger requires.
   */
  unlocked?: readonly UnlockId[];
  cash?: Int;
}

/** The cold-open layout (§0.4 u00): Gallery at its pre-placed cell with the Cork Popper and a full tin. */
export function coldOpenState(d: SimDefs, seed: Int, opts: StartOptions = {}): SimState {
  const s = emptyState(d, seed, opts.bounds ?? 0);
  const c = makeCtx(s, d, []);
  const p = d.buildings.gallery.preplaced;
  const g = makeGallery(s, d, p.x, p.y, 'N');
  g.gun = { id: newId(s), fam: 'gun', tier: p.gunTier };
  addBuilding(c, g);
  book(s, d, 'gun', p.gunTier, 'granted', 1);
  discover(c, 'gun', p.gunTier);
  for (const u of d.unlocks) if (u.trigger.type === 'start') applyUnlock(c, u.id);
  if (opts.afterU01 === true) {
    emptyPracticeTin(s);
    applyUnlock(c, 'u01');
  }
  if (opts.unlocked !== undefined) {
    checkUnlockList(d, opts.unlocked);
    for (const u of d.unlocks) {
      if (!opts.unlocked.includes(u.id)) continue;
      if (u.trigger.type === 'practiceEmpty') emptyPracticeTin(s);
      applyUnlock(c, u.id);
    }
  }
  if (opts.unlockAll === true) applyAllUnlocks(c);
  if (opts.cash !== undefined) s.wallet.cash = opts.cash;
  return s;
}

/**
 * Throws unless `ids` are known, version-active unlocks and prefix-closed along `after` (every listed
 * unlock's prerequisite is listed too: a mid-progression start still has nested masks).
 */
export function checkUnlockList(d: SimDefs, ids: readonly UnlockId[]): void {
  for (const id of ids) {
    const u = d.unlocks.find((x) => x.id === id);
    if (u === undefined) throw new RangeError(`unknown unlock ${id}`);
    if (!isActive(d, u.version))
      throw new RangeError(`${id} is not active in v${d.contentVersion}`);
    if (u.after !== null && !ids.includes(u.after)) {
      throw new RangeError(`${id} needs ${u.after} (unlocks nest along their prerequisites)`);
    }
  }
}

function checkLevel(level: Int, max: Int): void {
  if (!Number.isInteger(level) || level < 1 || level > max) {
    throw new RangeError(`level ${level} outside 1..${max}`);
  }
}

function openCtx(s: SimState, d: SimDefs, x: Int, y: Int): Ctx {
  const c = makeCtx(s, d, []);
  if (!isFree(c.occ, x, y)) throw new RangeError(`cell (${x},${y}) is not a free open cell`);
  return c;
}

/** TEST/DEBUG path: puts a deposit on a free cell (M3 adds the shop purchase). */
export function placeDeposit(
  s: SimState,
  d: SimDefs,
  ore: OreId,
  x: Int,
  y: Int,
  level: Int = 1,
): DepositB {
  checkLevel(level, d.depositRules.oddsByLevel.length);
  const c = openCtx(s, d, x, y);
  const b = makeDeposit(s, ore, x, y, level);
  addBuilding(c, b);
  return b;
}

/** TEST/DEBUG path: puts a finished (non-ghost) drill on a free cell (M3 adds buy/place and ghosts). */
export function placeDrill(
  s: SimState,
  d: SimDefs,
  x: Int,
  y: Int,
  dir: Dir,
  level: Int = 1,
): DrillB {
  checkLevel(level, d.buildings.drill.hitTicks.length);
  const c = openCtx(s, d, x, y);
  const b = makeDrill(s, x, y, dir, level);
  addBuilding(c, b);
  return b;
}

/** TEST/DEBUG path: a loose item on a free cell, booked as `granted` (like `dbg spawn`). */
export function placeItem(
  s: SimState,
  d: SimDefs,
  fam: FamId,
  tier: Int,
  x: Int,
  y: Int,
): FloorItem {
  if (!isValidItem(d, fam, tier)) throw new RangeError(`${fam} T${tier} is not active`);
  const c = openCtx(s, d, x, y);
  const it = addItem(c, fam, tier, x, y);
  book(s, d, fam, tier, 'granted', 1);
  discover(c, fam, tier);
  return it;
}

/**
 * Records unlock ids WITHOUT applying their state effects (scenario boards: feature flags on, no practice
 * tin, no Lead Vein rising): every version-active unlock, or exactly `ids` (prefix-closed along `after`).
 * The goal chip follows the latest one with chips.
 */
export function grantFlags(s: SimState, d: SimDefs, ids?: readonly UnlockId[]): void {
  if (ids !== undefined) checkUnlockList(d, ids);
  d.unlocks.forEach((u, i) => {
    if (!isActive(d, u.version) || s.progress.unlocked.includes(u.id)) return;
    if (ids !== undefined && !ids.includes(u.id)) return;
    s.progress.unlocked.push(u.id);
    if (u.chips.length > 0 && i > s.progress.goal) s.progress.goal = i;
  });
}

/** An item stored in a building by a test / scenario (booked as granted, discovered). */
function grantRef(c: Ctx, fam: FamId, tier: Int): ItemRef {
  if (!isValidItem(c.d, fam, tier)) throw new RangeError(`${fam} T${tier} is not active`);
  book(c.s, c.d, fam, tier, 'granted', 1);
  discover(c, fam, tier);
  return { id: newId(c.s), fam, tier };
}

export interface PlaceSpec {
  type: BType;
  x: Int;
  y: Int;
  dir?: Dir;
  level?: Int;
  /** Deposits. */
  ore?: OreId;
  /** Chutes: the side items come in from (default: opposite of `dir`). */
  inDir?: Dir;
  /** A first-build ghost (nothing delivered). */
  ghost?: boolean;
  paused?: boolean;
  /** Packers: the chosen target tier. */
  target?: Int;
  /** Crates: family and per-tier counts (booked as granted). */
  fam?: FamId;
  counts?: Int[];
  /** Processors: items per input slot (in slot order), finished items in `out`, Smelter heat. */
  slots?: { fam: FamId; tier: Int }[][];
  out?: { fam: FamId; tier: Int }[];
  heat?: Int;
  /** Packers: buffered items (all one family; sets the lock). */
  buf?: { fam: FamId; tier: Int }[];
  /** Galleries: the mounted gun tier (booked as granted). */
  gun?: Int;
  paid?: Int;
}

/**
 * TEST/SCENARIO path: puts any building on a free cell, optionally pre-filled (booked as granted). A
 * finished (non-ghost) building marks its type as built, like a completed first build.
 */
export function placeBuilding(s: SimState, d: SimDefs, p: PlaceSpec): Building {
  const level = p.level ?? 1;
  const c = openCtx(s, d, p.x, p.y);
  let b: Building;
  if (p.type === 'deposit') {
    if (p.ore === undefined) throw new RangeError('a deposit needs an ore');
    checkLevel(level, d.depositRules.oddsByLevel.length);
    b = makeDeposit(s, p.ore, p.x, p.y, level);
  } else {
    b = makeOfType(s, d, p.type, p.x, p.y, p.dir ?? DEFAULT_DIR, level);
  }
  if (b.type === 'chute' && p.inDir !== undefined) b.inDir = p.inDir;
  if (p.paid !== undefined) b.paid = p.paid;
  if (b.type === 'packer' && p.target !== undefined) b.target = p.target;
  if (b.type === 'packer') {
    for (const it of p.buf ?? []) {
      if (b.lockFam !== null && b.lockFam !== it.fam)
        throw new RangeError('a Packer holds one family');
      b.lockFam = it.fam;
      b.buf.push(grantRef(c, it.fam, it.tier));
    }
  }
  if (b.type === 'crate' && p.fam !== undefined && p.counts !== undefined) {
    let total = 0;
    for (const n of p.counts) total += n;
    if (total > (d.buildings.crate.capacity[level - 1] ?? 0)) {
      throw new RangeError(
        `crate L${level} holds at most ${d.buildings.crate.capacity[level - 1]}`,
      );
    }
    b.fam = p.fam;
    for (let t = 1; t <= p.counts.length; t++) {
      const n = p.counts[t - 1] ?? 0;
      if (n <= 0) continue;
      if (!isValidItem(d, p.fam, t)) throw new RangeError(`${p.fam} T${t} is not active`);
      b.counts[t - 1] = n;
      book(s, d, p.fam, t, 'granted', 1, n);
      discover(c, p.fam, t);
    }
  }
  if (isProcessorType(b.type)) {
    const pb = b as ProcessorB;
    const fams = slotFams(d, pb);
    (p.slots ?? []).forEach((list, i) => {
      for (const it of list) {
        if (fams[i] !== it.fam) throw new RangeError(`slot ${i} of ${pb.type} takes ${fams[i]}`);
        pb.slots[i]?.push(grantRef(c, it.fam, it.tier));
      }
    });
    for (const it of p.out ?? []) pb.out.push(grantRef(c, it.fam, it.tier));
    if (p.heat !== undefined) pb.heat = p.heat;
  }
  if (b.type === 'gallery' && p.gun !== undefined) b.gun = grantRef(c, 'gun', p.gun);
  if (p.ghost === true) {
    b.ghost = newGhost(d, b.type);
    raiseStall(c, b, 'GHOST');
  } else if (!s.progress.firstBuilt.includes(b.type)) {
    s.progress.firstBuilt.push(b.type);
  }
  if (p.paused === true) {
    b.paused = true;
    raiseStall(c, b, 'PAUSED');
  }
  addBuilding(c, b);
  return b;
}

/** Deep copy (the state is plain JSON by contract). */
export function cloneState(s: SimState): SimState {
  return JSON.parse(JSON.stringify(s)) as SimState;
}
